// Profile page (steps 8 + 9 of the implementation plan).
//
// Step 9 layered on edit mode + validation rendering. State lives in a
// reducer + context (see ./profile/state.ts) so EditableField components can
// pull/push values without prop-drilling.

import { lazy, Suspense, useReducer, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Lazy: each tab is only fetched when the visitor actually opens it, so the
// main bundle carries just the Overview section + tab bar (perf-smoke budget).
const TimeOffTab = lazy(() =>
  import('./profile/tabs/timeOff/TimeOffTab').then((m) => ({ default: m.TimeOffTab })),
);
const TasksTab = lazy(() =>
  import('./profile/tabs/tasks/TasksTab').then((m) => ({ default: m.TasksTab })),
);
const AttendanceTab = lazy(() =>
  import('./profile/tabs/attendance/AttendanceTab').then((m) => ({ default: m.AttendanceTab })),
);
const DocsTab = lazy(() =>
  import('./profile/tabs/docs/DocsTab').then((m) => ({ default: m.DocsTab })),
);
const ReviewsTab = lazy(() =>
  import('./profile/tabs/reviews/ReviewsTab').then((m) => ({ default: m.ReviewsTab })),
);
const ContractTab = lazy(() => import('./profile/tabs/contract/ContractTab'));
const NotificationsTab = lazy(() => import('./profile/tabs/notifications/NotificationsTab'));
import { ArrowLeft, Camera, Check, LayoutGrid, Sun, Clock, CheckSquare, FileSignature, FileText, PenTool, Bell, X, Search } from 'lucide-react';
import { useRef } from 'react';
import { api, ApiError } from '../api/client';
import type {
  EmployeeProfile,
  PublicProfile,
  ProfileGetResponse,
  ProfileUpdateRequest,
  ProfileUpdateResponse,
  WorkHistoryEntry,
  ProfilePermissions,
} from '@pa/shared';
import {
  ProfileEditingContext,
  initialEditState,
  parseSaveError,
  profileEditReducer,
  sectionsWithErrors,
  useProfileEditing,
  currentValue,
  filterSectionsByQuery,
  type AdminConfigLists,
  type AdminConfigOption,
  resolveProfileTab,
} from './profile/state';

// Country codes from profile-page-spec.md §7.2 — used by the phone composite.
const COUNTRY_CODES: ReadonlyArray<{ code: string; flag: string }> = [
  { code: '+45', flag: '🇩🇰' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+44', flag: '🇬🇧' },
  { code: '+46', flag: '🇸🇪' },
  { code: '+47', flag: '🇳🇴' },
  { code: '+358', flag: '🇫🇮' },
  { code: '+49', flag: '🇩🇪' },
  { code: '+33', flag: '🇫🇷' },
  { code: '+31', flag: '🇳🇱' },
  { code: '+32', flag: '🇧🇪' },
  { code: '+34', flag: '🇪🇸' },
  { code: '+39', flag: '🇮🇹' },
  { code: '+351', flag: '🇵🇹' },
  { code: '+41', flag: '🇨🇭' },
  { code: '+43', flag: '🇦🇹' },
  { code: '+48', flag: '🇵🇱' },
  { code: '+420', flag: '🇨🇿' },
  { code: '+36', flag: '🇭🇺' },
  { code: '+30', flag: '🇬🇷' },
  { code: '+353', flag: '🇮🇪' },
  { code: '+61', flag: '🇦🇺' },
  { code: '+64', flag: '🇳🇿' },
  { code: '+81', flag: '🇯🇵' },
  { code: '+82', flag: '🇰🇷' },
  { code: '+86', flag: '🇨🇳' },
  { code: '+91', flag: '🇮🇳' },
  { code: '+55', flag: '🇧🇷' },
  { code: '+52', flag: '🇲🇽' },
  { code: '+27', flag: '🇿🇦' },
  { code: '+971', flag: '🇦🇪' },
];

const TABS = [
  { key: 'overview', label: 'Profile Overview', icon: LayoutGrid },
  { key: 'timeOff', label: 'Time off', icon: Sun },
  { key: 'attendance', label: 'Attendance', icon: Clock },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare },
  { key: 'docs', label: 'Docs', icon: FileText },
  { key: 'contract', label: 'Contract', icon: FileSignature },
  { key: 'reviews', label: 'Reviews', icon: PenTool },
  { key: 'notifications', label: 'Notifications', icon: Bell },
] as const;

const SECTION_LABELS: Record<string, string> = {
  overview: 'Overview',
  personalData: 'Personal Data',
  address: 'Address',
  identification: 'Identification',
  someConsent: 'SoMe Consent',
  work: 'Work',
  payroll: 'Payroll',
  workingVisa: 'Working Visa',
  tcField: 'T&C Field',
  itHardware: 'IT Hardware',
  termination: 'Termination/Resignation',
};

const SECTION_ORDER: string[] = [
  'overview',
  'personalData',
  'address',
  'identification',
  'someConsent',
  'work',
  'payroll',
  'workingVisa',
  'tcField',
  'itHardware',
  'termination',
];

export default function ProfileView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editState, dispatch] = useReducer(profileEditReducer, initialEditState);
  // Active tab is URL-driven via `?tab=timeOff` etc. so a deep link
  // from a notification can land directly on the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  // Case-insensitive so links already delivered with `?tab=timeoff`
  // still open the Time off tab (T-325).
  const activeTab = resolveProfileTab(searchParams.get('tab'));
  const handleTabChange = (key: string) => {
    if (key === 'overview') {
      // Drop the param when defaulting back so the URL stays clean.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      next.set('tab', key);
      setSearchParams(next, { replace: true });
    }
  };

  const query = useQuery({
    queryKey: ['profile', id],
    queryFn: () => api.get<ProfileGetResponse>(`/api/employees/${id}/profile`),
    enabled: !!id,
  });

  // Fetch admin-config option lists when an admin enters edit mode. The
  // `enabled` guard means non-admin viewers never hit the admin-only
  // /api/admin-config endpoints.
  const isAdmin = query.data?.permissions.viewerRelation === 'admin';
  const adminConfigQuery = useQuery({
    queryKey: ['admin-config-bundle'],
    queryFn: async () => {
      const [sites, teams, studios, functions] = await Promise.all([
        api.get<{ items: { id: string; name: string; archived: boolean }[] }>(
          '/api/admin-config/sites',
        ),
        api.get<{ items: { id: string; name: string; archived: boolean }[] }>(
          '/api/admin-config/teams',
        ),
        api.get<{ items: { id: string; name: string; archived: boolean }[] }>(
          '/api/admin-config/studios',
        ),
        api.get<{ items: { id: string; name: string; archived: boolean }[] }>(
          '/api/admin-config/functions',
        ),
      ]);
      const toOptions = (items: { id: string; name: string; archived: boolean }[]) =>
        items.map((i) => ({ id: i.id, label: i.name, archived: i.archived }));
      const lists: AdminConfigLists = {
        sites: toOptions(sites.items),
        teams: toOptions(teams.items),
        studios: toOptions(studios.items),
        functions: toOptions(functions.items),
      };
      return lists;
    },
    enabled: isAdmin && editState.mode === 'edit',
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: (draft: ProfileUpdateRequest) =>
      api.patch<ProfileUpdateResponse>(`/api/employees/${id}/profile`, draft),
    onMutate: () => dispatch({ type: 'SAVING' }),
    onSuccess: () => {
      dispatch({ type: 'SAVE_SUCCEEDED' });
      queryClient.invalidateQueries({ queryKey: ['profile', id] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (err: unknown) => {
      const body = err instanceof ApiError ? err.body : null;
      const { fieldErrors, formError } = parseSaveError(body);
      dispatch({ type: 'SAVE_FAILED', fieldErrors, formError });
    },
  });

  if (query.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="max-w-4xl">
        <button
          onClick={() => navigate('/employees')}
          className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-6 mt-4">
          Failed to load profile.
        </div>
      </div>
    );
  }

  const { profile, workHistory, permissions } = query.data;
  const isPublic = permissions.viewerRelation === 'other';
  // Cast to EmployeeProfile for the editing context — only ever consumed
  // when isPublic is false, so the cast is safe.
  const editableProfile = isPublic ? null : (profile as EmployeeProfile);
  const canEdit = new Set(permissions.canEdit);
  const errorSections = sectionsWithErrors(editState.fieldErrors);

  function handleEdit() {
    if (editableProfile) dispatch({ type: 'START_EDIT', snapshot: editableProfile });
  }
  function handleCancel() {
    dispatch({ type: 'CANCEL' });
  }
  function handleSave() {
    save.mutate(editState.draft);
  }
  function handleConfirmDiscard() {
    if (editState.mode !== 'edit') {
      navigate('/employees');
      return;
    }
    if (window.confirm('Discard unsaved changes?')) {
      dispatch({ type: 'CANCEL' });
      navigate('/employees');
    }
  }

  return (
    <ProfileEditingContext.Provider
      value={{
        state: editState,
        dispatch,
        profile: editableProfile,
        canEdit,
        adminConfig: adminConfigQuery.data ?? null,
      }}
    >
      <div className="max-w-6xl -m-8">
        <ProfileHeader
          profile={profile}
          permissions={permissions}
          editing={editState.mode === 'edit'}
          saving={editState.saving}
          canEditAny={canEdit.size > 0}
          onBack={handleConfirmDiscard}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
        />
        {editState.formError && (
          <div className="bg-rose-50 border-b border-rose-200 text-rose-700 px-8 py-3 text-sm font-medium">
            {editState.formError}
          </div>
        )}
        <TabBar
          isEditing={editState.mode === 'edit'}
          active={activeTab}
          onChange={handleTabChange}
        />
        {activeTab === 'timeOff' ? (
          <Suspense fallback={<TabLoader />}>
            <TimeOffTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'tasks' ? (
          <Suspense fallback={<TabLoader />}>
            <TasksTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'attendance' ? (
          <Suspense fallback={<TabLoader />}>
            <AttendanceTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'docs' ? (
          <Suspense fallback={<TabLoader />}>
            <DocsTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'contract' ? (
          <Suspense fallback={<TabLoader />}>
            <ContractTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'reviews' ? (
          <Suspense fallback={<TabLoader />}>
            <ReviewsTab employeeId={profile.id} />
          </Suspense>
        ) : activeTab === 'notifications' ? (
          <div className="p-10 bg-white">
            <Suspense fallback={<TabLoader />}>
              <NotificationsTab />
            </Suspense>
          </div>
        ) : (
          <div className="flex">
            <SubSidebar
              visibleSections={permissions.visibleSections}
              errorSections={errorSections}
            />
            <div className="flex-1 bg-white p-10 max-w-4xl">
              {editState.mode === 'edit' && <EditingBanner />}
              {isPublic ? (
                <PublicSections profile={profile as PublicProfile} />
              ) : (
                <FullSections
                  profile={profile as EmployeeProfile}
                  workHistory={workHistory}
                  permissions={permissions}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </ProfileEditingContext.Provider>
  );
}

// ── Header ──────────────────────────────────────────────────────────

function ProfileHeader({
  profile,
  editing,
  saving,
  canEditAny,
  onBack,
  onEdit,
  onCancel,
  onSave,
}: {
  profile: EmployeeProfile | PublicProfile;
  permissions: ProfilePermissions;
  editing: boolean;
  saving: boolean;
  canEditAny: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const initials =
    ((profile.firstName ?? '?').charAt(0) + (profile.lastName ?? '').charAt(0)).toUpperCase();
  const teamName = (profile as PublicProfile).teamName ?? null;
  return (
    <div className="bg-white border-b border-slate-100 px-8 py-6 flex items-center gap-6">
      <button
        onClick={onBack}
        className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        aria-label="Back to employees"
      >
        <ArrowLeft size={20} />
      </button>
      <ProfileAvatar profile={profile} initials={initials} canEditAny={canEditAny} />
      <div className="flex-1">
        <h1 className="text-2xl font-bold text-slate-800">
          {profile.displayName ||
            `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() ||
            '—'}
        </h1>
        {teamName && <p className="text-sm text-slate-500">{teamName},</p>}
        {profile.status === 'active' && (
          <p className="flex items-center gap-1.5 mt-1 text-xs font-bold uppercase tracking-wider text-emerald-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Employed
          </p>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
          >
            <X size={15} /> Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="bg-[#6344C6] hover:bg-[#5438b0] text-white text-sm font-bold px-4 py-2 rounded-lg shadow-sm flex items-center gap-1.5 disabled:opacity-60"
          >
            <Check size={15} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      ) : (
        <button
          onClick={onEdit}
          disabled={!canEditAny}
          className="border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          title={canEditAny ? 'Edit profile' : 'You do not have permission to edit this profile'}
        >
          Edit Profile
        </button>
      )}
    </div>
  );
}

// ── Editing banner ──────────────────────────────────────────────────

function EditingBanner() {
  return (
    <div className="bg-[#6344C6]/5 border border-[#6344C6]/20 rounded-xl px-4 py-3 mb-8 flex items-center gap-3">
      <span className="w-2 h-2 rounded-full bg-[#6344C6] animate-pulse" />
      <p className="text-sm text-[#6344C6]">
        <span className="font-medium">Editing mode</span> — changes apply to all fields below.
        Click <span className="font-bold">Save changes</span> when done.
      </p>
    </div>
  );
}

function TabLoader() {
  return (
    <div className="flex items-center justify-center bg-white p-24 text-sm text-slate-400">
      Loading…
    </div>
  );
}

// ── Tab bar / sidebar ───────────────────────────────────────────────

// Tab bar tooltip rules (profile-page-spec.md §8):
//   - In view mode, the four non-active tabs are stubs → tooltip "Coming soon".
//   - In edit mode, ALL non-active tabs are blocked because switching would
//     drop the draft. Tooltip is "Save or cancel to switch tabs" regardless of
//     stub-ness (the user needs to know the edit, not the feature, is the
//     reason). Active tab gets no tooltip.
// Tabs that have a real implementation today. Other entries in TABS
// are kept in the bar for visual consistency but stay disabled with
// the "Coming soon" tooltip until their implementation lands.
const ENABLED_TABS = new Set<string>([
  'overview',
  'timeOff',
  'tasks',
  'attendance',
  'docs',
  // Employment contracts (T-313/T-316).
  'contract',
]);

function TabBar({
  isEditing,
  active,
  onChange,
}: {
  isEditing: boolean;
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="bg-white border-b border-slate-100 px-8 flex gap-8">
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = active === tab.key;
        const isEnabled = ENABLED_TABS.has(tab.key);
        const tooltip = isActive
          ? ''
          : isEditing
            ? 'Save or cancel to switch tabs'
            : isEnabled
              ? ''
              : 'Coming soon';
        return (
          <button
            key={tab.key}
            disabled={isEditing || !isEnabled}
            data-testid={`profile-tab-${tab.key}`}
            onClick={() => isEnabled && !isEditing && onChange(tab.key)}
            className={`relative flex items-center gap-2 py-4 text-sm font-medium ${
              isActive
                ? 'text-slate-900 cursor-default'
                : isEnabled && !isEditing
                  ? 'text-slate-500 hover:text-slate-700 cursor-pointer'
                  : 'text-slate-400 cursor-not-allowed'
            }`}
            title={tooltip}
          >
            <Icon size={16} /> {tab.label}
            {isActive && (
              <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-slate-900" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// Sub-sidebar with the live "Search fields" input per profile-page-spec.md §6a.
// Typing filters the section list to entries whose title or any field label
// matches (case-insensitive). Sections with no match disappear; an empty
// query restores the full visible list. Match logic lives in
// `filterSectionsByQuery` (./profile/state.ts) so it's unit-testable.
function SubSidebar({
  visibleSections,
  errorSections,
}: {
  visibleSections: string[];
  errorSections: Set<string>;
}) {
  const [query, setQuery] = useState('');
  const ordered = SECTION_ORDER.filter((k) => visibleSections.includes(k));
  const filtered = filterSectionsByQuery(ordered, query, SECTION_LABELS);

  return (
    <aside className="w-64 bg-slate-50/30 border-r border-slate-100 p-4 space-y-3">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fields"
          aria-label="Search profile fields"
          data-testid="profile-section-search"
          className="w-full text-sm bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 placeholder:text-slate-400 focus:outline-none focus:border-[#6344C6] focus:ring-2 focus:ring-[#6344C6]/10"
        />
      </div>
      <div className="space-y-1">
        {filtered.length === 0 ? (
          <p className="text-xs text-slate-400 px-3 py-2" data-testid="profile-section-search-empty">
            No sections match &quot;{query.trim()}&quot;.
          </p>
        ) : (
          filtered.map((k) => (
            <a
              key={k}
              href={`#section-${k}`}
              className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-slate-100 hover:text-slate-900 ${
                k === 'termination' ? 'text-red-600 hover:text-red-700' : 'text-slate-500'
              }`}
            >
              <span>{SECTION_LABELS[k]}</span>
              {errorSections.has(k) && (
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-label="has errors" />
              )}
            </a>
          ))
        )}
      </div>
    </aside>
  );
}

// ── Public-relation render (no editing) ─────────────────────────────

function PublicSections({ profile }: { profile: PublicProfile }) {
  // Plain static rendering — "other" relation has no edit path.
  return (
    <Section id="overview" title="Overview">
      <FieldGrid>
        <StaticField label="Display name" value={profile.displayName} />
        <StaticField label="First name" value={profile.firstName} required />
        <StaticField label="Last name" value={profile.lastName} required />
        <StaticField label="Middle name" value={profile.middleName} />
        <StaticField label="Work email" value={profile.workEmail} required />
        <StaticField label="Job title" value={profile.jobTitle} />
        <StaticField label="Team" value={profile.teamName} />
        <StaticField label="Site" value={profile.siteName} />
        <StaticField label="Studio" value={profile.studioName} />
      </FieldGrid>
    </Section>
  );
}

// ── Full sections (admin / self) ────────────────────────────────────

function FullSections({
  profile,
  workHistory,
  permissions,
}: {
  profile: EmployeeProfile;
  workHistory: WorkHistoryEntry[];
  permissions: ProfilePermissions;
}) {
  const has = (k: string) => permissions.visibleSections.includes(k);
  return (
    <div className="space-y-12">
      {has('overview') && (
        <Section id="overview" title="Overview">
          <FieldGrid>
            <EditableDisplayNameField label="Display name" displayName={profile.displayName} customized={profile.displayNameCustomized} />
            <EditableField label="First name" fieldKey="firstName" required />
            <EditableField label="Last name" fieldKey="lastName" required />
            <EditableField label="Middle name" fieldKey="middleName" />
            <EditableField label="Work email" fieldKey="workEmail" required type="email" />
            <EditableField label="Employee ID" fieldKey="employeeIdCode" />
            <EditableField label="Start date" fieldKey="startDate" required type="date" />
            <EditableAdminConfigField label="Site" fieldKey="siteId" list="sites" displayName={profile.siteName} />
            <StaticField label="Approver time off" value={profile.approverTimeOffEmployeeId} />
            <StaticField label="Team Lead" value={profile.teamLeadEmployeeId} />
            <StaticField label="Functional Lead" value={profile.functionalLeadEmployeeId} />
            <EditableField label="Job title" fieldKey="jobTitle" />
            <EditableAdminConfigField label="Team" fieldKey="teamId" list="teams" displayName={profile.teamName} />
            <EditableAdminConfigField label="Studio" fieldKey="studioId" list="studios" displayName={profile.studioName} />
          </FieldGrid>
        </Section>
      )}
      {has('personalData') && (
        <Section id="personalData" title="Personal Data">
          <FieldGrid>
            <EditableField label="Date of birth" fieldKey="dateOfBirth" type="date" />
            <EditableField
              label="Legal gender"
              fieldKey="legalGender"
              type="select"
              options={['Female', 'Male', 'Non-binary', 'Prefer not to say']}
            />
            <EditableField
              label="Pronouns"
              fieldKey="pronouns"
              type="select"
              options={[
                'she/her',
                'he/him',
                'they/them',
                'she/they',
                'he/they',
                'xe/xem',
                'Prefer not to say',
              ]}
            />
            <EditableField label="Personal email" fieldKey="personalEmail" type="email" />
            <EditablePhoneField
              label="Personal mobile"
              countryFieldKey="personalMobileCountry"
              numberFieldKey="personalMobileNumber"
            />
            <EditableField label="Emergency contact" fieldKey="emergencyContactName" />
            <EditablePhoneField
              label="Emergency contact phone"
              countryFieldKey="emergencyContactCountry"
              numberFieldKey="emergencyContactNumber"
            />
          </FieldGrid>
        </Section>
      )}
      {has('address') && (
        <Section id="address" title="Address">
          <FieldGrid>
            <EditableField label="Street" fieldKey="addressStreet" />
            <EditableField label="City & Zip" fieldKey="addressCityZip" />
            <EditableField label="Country" fieldKey="addressCountry" />
          </FieldGrid>
        </Section>
      )}
      {has('identification') && (
        <Section id="identification" title="Identification">
          <FieldGrid>
            <EditableField label="Passport number" fieldKey="passportNumber" />
            <EditableField label="CPR" fieldKey="cpr" />
            <EditableField
              label="Passport expiration date"
              fieldKey="passportExpirationDate"
              type="date"
            />
          </FieldGrid>
        </Section>
      )}
      {has('someConsent') && (
        <Section id="someConsent" title="SoMe Consent">
          <p className="text-sm text-slate-500 italic mb-6">
            Do you allow Tactile to use photos where you are present for uploading on various
            social media
          </p>
          <FieldGrid>
            <EditableField
              label="SoMe consent"
              fieldKey="someConsent"
              type="select"
              options={['Yes', 'No']}
            />
          </FieldGrid>
        </Section>
      )}
      {has('work') && (
        <Section id="work" title="Work">
          <FieldGrid>
            <EditableField label="Employee ID" fieldKey="employeeIdCode" required />
            <EditableAdminConfigField label="Function" fieldKey="functionId" list="functions" displayName={profile.functionName} required />
            <StaticField label="Tenure" value={profile.tenure?.displayText ?? null} />
            <EditableField
              label="Contract type"
              fieldKey="contractType"
              type="select"
              options={['Employee', 'Contractor']}
            />
            <EditableField
              label="Working hours"
              fieldKey="workingHours"
              type="select"
              options={['Full time', 'Part time', 'Hourly']}
            />
          </FieldGrid>
          {permissions.visibleSections.includes('workHistory') && (
            <WorkHistoryTable entries={workHistory} />
          )}
        </Section>
      )}
      {has('payroll') && (
        <Section id="payroll" title="Payroll">
          <FieldGrid>
            <EditableField
              label="Additional pension contribution"
              fieldKey="additionalPensionContribution"
              type="number"
            />
            <EditableField
              label="Employee pension contribution"
              fieldKey="employeePensionContribution"
              type="number"
            />
            <EditableField label="Base salary" fieldKey="baseSalary" required type="number" />
            <EditableField
              label="Salary type"
              fieldKey="salaryType"
              type="select"
              options={['Monthly']}
            />
            <EditableField
              label="Salary pay frequency"
              fieldKey="salaryPayFrequency"
              required
              type="select"
              options={['Monthly']}
            />
            <EditableField
              label="Currency"
              fieldKey="currency"
              type="select"
              options={['DKK', 'EUR', 'USD', 'GBP', 'SEK', 'NOK']}
            />
          </FieldGrid>
          <p className="text-sm text-slate-400 italic mt-6">No variable pay records</p>
        </Section>
      )}
      {has('workingVisa') && (
        <Section id="workingVisa" title="Working Visa">
          <FieldGrid>
            <EditableField
              label="Visa required"
              fieldKey="visaRequired"
              required
              type="select"
              options={['Yes', 'No']}
              boolean
            />
            <EditableField
              label="Application date"
              fieldKey="visaApplicationDate"
              type="date"
            />
            <EditableField
              label="Working visa valid from"
              fieldKey="visaValidFrom"
              type="date"
            />
            <EditableField
              label="Expiration date"
              fieldKey="visaExpirationDate"
              type="date"
            />
            <EditableField label="Disco code" fieldKey="discoCode" />
          </FieldGrid>
          {profile.visaRequired && (
            <p className="text-[11px] text-amber-600 font-medium mt-4">
              ⚡ Visa Application task is active in onboarding
            </p>
          )}
        </Section>
      )}
      {has('tcField') && (
        <Section id="tcField" title="T&C Field">
          <FieldGrid>
            <EditableField
              label="Seniority level"
              fieldKey="seniorityLevel"
              type="select"
              options={['Junior', 'Mid', 'Senior', 'Lead', 'Principal']}
            />
            <EditableField label="Advancement level" fieldKey="advancementLevel" />
          </FieldGrid>
        </Section>
      )}
      {has('itHardware') && (
        <Section id="itHardware" title="IT Hardware">
          <FieldGrid>
            <EditableField label="Cheqroom link" fieldKey="cheqroomLink" />
            <EditableField label="Hardware" fieldKey="hardwareNotes" />
          </FieldGrid>
        </Section>
      )}
      {has('termination') && (
        <Section
          id="termination"
          title="Termination/Resignation"
          titleClassName="text-red-600"
        >
          <FieldGrid>
            <StaticField label="Notice period" value={profile.noticePeriod?.displayText ?? null} />
            <EditableField label="Last day at work" fieldKey="lastDayAtWork" type="date" />
            <EditableField
              label="Last contractual day"
              fieldKey="lastContractualDay"
              type="date"
            />
          </FieldGrid>
        </Section>
      )}
    </div>
  );
}

// ── Reusable bits ───────────────────────────────────────────────────

function Section({
  id,
  title,
  children,
  titleClassName,
}: {
  id: string;
  title: string;
  children: ReactNode;
  titleClassName?: string;
}) {
  return (
    <section id={`section-${id}`}>
      <h3 className={`text-xl font-bold mb-8 ${titleClassName ?? 'text-slate-800'}`}>{title}</h3>
      {children}
    </section>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-12 gap-y-8">{children}</div>;
}

function StaticField({
  label,
  value,
  required,
}: {
  label: string;
  value: string | number | null | undefined;
  required?: boolean;
}) {
  return (
    <div>
      <Label label={label} required={required} />
      <p className="text-sm text-slate-800">{formatValue(value)}</p>
    </div>
  );
}

interface EditableFieldProps {
  label: string;
  fieldKey: keyof ProfileUpdateRequest;
  required?: boolean;
  type?: 'text' | 'email' | 'date' | 'number' | 'select';
  options?: ReadonlyArray<string>;
  // For visaRequired: persist as boolean even though the UI shows "Yes"/"No".
  boolean?: boolean;
}

function EditableField(props: EditableFieldProps) {
  const { label, fieldKey, required, type = 'text', options, boolean: asBool } = props;
  const { state, dispatch, profile, canEdit } = useProfileEditing();
  const isEditing = state.mode === 'edit' && canEdit.has(fieldKey as string);
  const errors = state.fieldErrors[fieldKey as string];
  const raw = currentValue(state, profile, fieldKey);
  const display = formatValueForUI(raw, asBool);

  if (!isEditing) {
    return (
      <div>
        <Label label={label} required={required} />
        <p className="text-sm text-slate-800">{display}</p>
      </div>
    );
  }

  const baseInputCls = `w-full text-sm bg-transparent border-b ${
    errors ? 'border-red-500' : 'border-slate-300'
  } focus:border-[#6344C6] focus:outline-none py-1`;

  function setValue(value: unknown) {
    dispatch({ type: 'SET_FIELD', key: fieldKey, value });
  }

  let input: ReactNode;
  if (type === 'select' && options) {
    input = (
      <select
        value={display === '—' ? '' : display}
        onChange={(e) => {
          const v = e.target.value;
          if (asBool) setValue(v === 'Yes' ? true : v === 'No' ? false : null);
          else setValue(v === '' ? null : v);
        }}
        className={baseInputCls}
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  } else if (type === 'date') {
    input = (
      <input
        type="date"
        value={typeof raw === 'string' && raw ? raw.slice(0, 10) : ''}
        onChange={(e) => setValue(e.target.value || null)}
        className={baseInputCls}
      />
    );
  } else if (type === 'number') {
    input = (
      <input
        type="number"
        value={typeof raw === 'number' || typeof raw === 'string' ? String(raw ?? '') : ''}
        onChange={(e) => {
          const v = e.target.value;
          setValue(v === '' ? null : Number(v));
        }}
        className={baseInputCls}
      />
    );
  } else {
    input = (
      <input
        type={type}
        value={typeof raw === 'string' ? raw : ''}
        onChange={(e) => setValue(e.target.value)}
        placeholder="—"
        className={baseInputCls}
      />
    );
  }

  return (
    <div>
      <Label label={label} required={required} />
      {input}
      {errors && errors.length > 0 && (
        <p className="text-xs text-red-600 mt-1">{errors[0]}</p>
      )}
    </div>
  );
}

// Avatar with upload affordance (camera button) when the viewer can edit
// the profile (admin or self). Uploads via POST /api/employees/:id/profile/avatar
// with the file read as base64. Updates immediately on success by
// invalidating the profile query.
function ProfileAvatar({
  profile,
  initials,
  canEditAny,
}: {
  profile: EmployeeProfile | PublicProfile;
  initials: string;
  canEditAny: boolean;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      // Read as base64 (strip the `data:image/png;base64,` prefix).
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const data = dataUrl.includes(',') ? dataUrl.split(',', 2)[1] : dataUrl;
      return api.post<{ ok: boolean; avatarUrl: string }>(
        `/api/employees/${profile.id}/profile/avatar`,
        { mimetype: file.type, data },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', profile.id] });
    },
  });

  // Cache-bust on successful upload so the <img> picks up the new bytes.
  const cacheBust = upload.isSuccess ? `?v=${upload.submittedAt ?? Date.now()}` : '';
  const avatarUrl = profile.avatarUrl ? `${profile.avatarUrl}${cacheBust}` : null;

  return (
    <div className="relative w-20 h-20">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-inner overflow-hidden ${
          avatarUrl ? 'bg-slate-200' : profile.avatarColor
        }`}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          initials
        )}
      </div>
      {canEditAny && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="absolute -bottom-1 -right-1 bg-white border border-slate-200 shadow-sm w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-50"
            aria-label="Upload avatar"
            title={
              upload.isError
                ? 'Upload failed (try a smaller PNG / JPEG / WebP under 512KB)'
                : 'Upload avatar (PNG / JPEG / WebP, ≤ 512KB)'
            }
          >
            <Camera size={12} />
          </button>
        </>
      )}
    </div>
  );
}

// Renders Site / Team / Studio / Function as an admin-config-fed select in
// edit mode, falling back to the resolved display name in view mode (or when
// the user can't edit it). The list is fetched lazily by ProfileView and
// passed through context as `adminConfig`. Archived options stay selectable
// only if the field already references one (so an existing archived value
// isn't accidentally cleared).
function EditableAdminConfigField({
  label,
  fieldKey,
  list,
  displayName,
  required,
}: {
  label: string;
  fieldKey: keyof ProfileUpdateRequest;
  list: 'sites' | 'teams' | 'studios' | 'functions';
  displayName: string | null;
  required?: boolean;
}) {
  const { state, dispatch, profile, canEdit, adminConfig } = useProfileEditing();
  const isEditing = state.mode === 'edit' && canEdit.has(fieldKey as string);
  const errors = state.fieldErrors[fieldKey as string];
  const draftId = currentValue(state, profile, fieldKey) as string | null | undefined;

  if (!isEditing) {
    return (
      <div>
        <Label label={label} required={required} />
        <p className="text-sm text-slate-800">{displayName ?? '—'}</p>
      </div>
    );
  }

  // Edit mode but lists not yet loaded → show a disabled placeholder.
  const options: AdminConfigOption[] = adminConfig?.[list] ?? [];
  const live = options.filter((o) => !o.archived);
  // Keep the currently-selected option visible even if archived, so the user
  // can see what's there and choose to change or keep it.
  const currentIsArchivedAndStillSelected =
    draftId &&
    options.some((o) => o.id === draftId && o.archived);

  const baseInputCls = `w-full text-sm bg-transparent border-b ${
    errors ? 'border-red-500' : 'border-slate-300'
  } focus:border-[#6344C6] focus:outline-none py-1`;

  return (
    <div>
      <Label label={label} required={required} />
      <select
        value={(draftId as string | null | undefined) ?? ''}
        onChange={(e) => {
          dispatch({ type: 'SET_FIELD', key: fieldKey, value: e.target.value || null });
        }}
        disabled={!adminConfig}
        className={baseInputCls}
      >
        <option value="">—</option>
        {live.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        {currentIsArchivedAndStillSelected && (
          <option key={draftId as string} value={draftId as string}>
            {options.find((o) => o.id === draftId)?.label} (archived)
          </option>
        )}
      </select>
      {!adminConfig && (
        <p className="text-xs text-slate-400 mt-1">Loading options…</p>
      )}
      {errors && errors.length > 0 && (
        <p className="text-xs text-red-600 mt-1">{errors[0]}</p>
      )}
    </div>
  );
}

// Composite phone field: country-code dropdown + number input, side by side.
// In view mode renders as `${country} ${number}` or `—`. In edit mode the
// two pieces are independent inputs that dispatch to two field keys.
function EditablePhoneField({
  label,
  countryFieldKey,
  numberFieldKey,
}: {
  label: string;
  countryFieldKey: keyof ProfileUpdateRequest;
  numberFieldKey: keyof ProfileUpdateRequest;
}) {
  const { state, dispatch, profile, canEdit } = useProfileEditing();
  const isEditing =
    state.mode === 'edit' &&
    (canEdit.has(countryFieldKey as string) || canEdit.has(numberFieldKey as string));
  const country = (currentValue(state, profile, countryFieldKey) as string | null | undefined) ?? '';
  const number = (currentValue(state, profile, numberFieldKey) as string | null | undefined) ?? '';
  const numberErrors = state.fieldErrors[numberFieldKey as string];
  const countryErrors = state.fieldErrors[countryFieldKey as string];
  const errors = numberErrors ?? countryErrors;

  if (!isEditing) {
    const display = [country, number].filter(Boolean).join(' ') || '—';
    return (
      <div>
        <Label label={label} />
        <p className="text-sm text-slate-800">{display}</p>
      </div>
    );
  }

  const baseInputCls = `text-sm bg-transparent border-b ${
    errors ? 'border-red-500' : 'border-slate-300'
  } focus:border-[#6344C6] focus:outline-none py-1`;

  return (
    <div>
      <Label label={label} />
      <div className="flex gap-2">
        <select
          value={country}
          onChange={(e) =>
            dispatch({
              type: 'SET_FIELD',
              key: countryFieldKey,
              value: e.target.value || null,
            })
          }
          className={`${baseInputCls} w-28`}
        >
          <option value="">—</option>
          {COUNTRY_CODES.map(({ code, flag }) => (
            <option key={code} value={code}>
              {code} {flag}
            </option>
          ))}
        </select>
        <input
          type="tel"
          value={number}
          onChange={(e) =>
            dispatch({
              type: 'SET_FIELD',
              key: numberFieldKey,
              value: e.target.value || null,
            })
          }
          placeholder="—"
          className={`${baseInputCls} flex-1`}
        />
      </div>
      {errors && errors.length > 0 && (
        <p className="text-xs text-red-600 mt-1">{errors[0]}</p>
      )}
    </div>
  );
}

// Display Name field with a "Reset to auto" affordance.
//
// The server supports `displayNameReset: true` to clear the customization
// flag and resume auto-deriving the display name from First/Middle/Last.
// This component dispatches `displayNameReset` into the draft when the
// "Reset to auto" button is clicked, alongside the regular `displayName`
// text input. When customized=false, the reset button is hidden (already
// auto-derived).
function EditableDisplayNameField({
  label,
  displayName,
  customized,
}: {
  label: string;
  displayName: string | null;
  customized: boolean;
}) {
  const { state, dispatch, profile, canEdit } = useProfileEditing();
  const isEditing = state.mode === 'edit' && canEdit.has('displayName');
  const errors = state.fieldErrors.displayName;
  const raw = currentValue(state, profile, 'displayName') as string | null | undefined;

  if (!isEditing) {
    return (
      <div>
        <Label label={label} />
        <p className="text-sm text-slate-800">{displayName ?? '—'}</p>
      </div>
    );
  }

  // Hide the reset button if the existing record is already auto-derived AND
  // the draft hasn't introduced a customization. Tracking the draft here
  // keeps the button visible right after a user types — and disappears once
  // they click reset or clear the field.
  const draftReset = (state.draft as Record<string, unknown>).displayNameReset === true;
  const showReset = (customized || (raw && raw.length > 0)) && !draftReset;

  const baseInputCls = `w-full text-sm bg-transparent border-b ${
    errors ? 'border-red-500' : 'border-slate-300'
  } focus:border-[#6344C6] focus:outline-none py-1`;

  return (
    <div>
      <Label label={label} />
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={typeof raw === 'string' ? raw : ''}
          onChange={(e) => {
            dispatch({ type: 'SET_FIELD', key: 'displayName', value: e.target.value });
            // Clear the reset flag if the user starts typing again.
            if (draftReset) {
              dispatch({ type: 'SET_FIELD', key: 'displayNameReset', value: false });
            }
          }}
          placeholder={draftReset ? '(auto-derived on save)' : '—'}
          disabled={draftReset}
          className={baseInputCls}
        />
        {showReset && (
          <button
            type="button"
            onClick={() => {
              dispatch({ type: 'SET_FIELD', key: 'displayName', value: null });
              dispatch({ type: 'SET_FIELD', key: 'displayNameReset', value: true });
            }}
            className="text-xs font-medium text-[#6344C6] hover:text-[#5235a8] whitespace-nowrap"
          >
            Reset to auto
          </button>
        )}
      </div>
      {errors && errors.length > 0 && (
        <p className="text-xs text-red-600 mt-1">{errors[0]}</p>
      )}
    </div>
  );
}

function Label({ label, required }: { label: string; required?: boolean }) {
  return (
    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </p>
  );
}

function WorkHistoryTable({ entries }: { entries: WorkHistoryEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="mt-8">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">
          Work history
        </p>
        <p className="text-sm text-slate-400 italic">No changes recorded yet.</p>
      </div>
    );
  }
  return (
    <div className="mt-8">
      <p className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Work history</p>
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-xs font-bold text-slate-400 uppercase tracking-wider text-left">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Field</th>
              <th className="px-4 py-3">Previous</th>
              <th className="px-4 py-3">New</th>
              <th className="px-4 py-3">Changed by</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                  {new Date(e.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-slate-700 font-medium">{e.fieldLabel}</td>
                <td className="px-4 py-3 text-slate-400 line-through">{e.previousValue ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#6344C6]/10 text-[#6344C6]">
                    {e.newValue ?? '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500">{e.changedByName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatValue(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    return v.slice(0, 10);
  }
  return v;
}

function formatValueForUI(v: unknown, asBool?: boolean): string {
  if (asBool) {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    return '—';
  }
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  if (typeof v === 'string') return v;
  return String(v);
}
